import {
  provideHttpClient,
  withInterceptors,
  withJsonpSupport,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router } from '@angular/router';
import { GlobalErrorHandler } from '@core/handlers/global-error-handler';
import { mainInterceptorFn } from '@core/interceptors';
import { FeatureFlagService } from '@core/services/feature-flag/feature-flag.service';
import * as State from '@core/state/';
import { envConfig, environment } from '@environments/environment';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';
import { DialogService } from 'primeng/dynamicdialog';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

import { LaraCustom } from '../theme/lara-custom';

import { metaReducers, reducers } from './reducers';

const featureFactory = (featureFlagService: FeatureFlagService) => () =>
  featureFlagService.initializeFeatureFlags();

export const initializeKeycloak = (keycloak: KeycloakService) => async () =>
  keycloak.init({
    config: {
      url: envConfig.keycloak.authority,
      realm: envConfig.keycloak.realm,
      clientId: envConfig.keycloak.clientId,
    },
    loadUserProfileAtStartUp: true,
    initOptions: {
      silentCheckSsoRedirectUri:
        window.location.origin + '/assets/silent-check-sso.html',
      checkLoginIframe: true,
      redirectUri: envConfig.keycloak.redirectUri,
    },
  });

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter([]),

    provideAnimations(),

    provideHttpClient(
      withInterceptors([mainInterceptorFn]),
      withJsonpSupport()
    ),

    providePrimeNG({
      theme: {
        preset: LaraCustom,
        options: {
          cssLayer: { name: 'primeng', order: 'reset, primeng, mercury' },
          darkModeSelector: '.dark',
        },
      },
      ripple: true,
    }),

    MessageService,
    ConfirmationService,
    DialogService,
    FeatureFlagService,
    { provide: 'environment', useValue: envConfig },

    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: featureFactory,
      deps: [FeatureFlagService, Sentry.TraceService],
      multi: true,
    },

    importProvidersFrom(
      ReactiveFormsModule,
      FormsModule,
      KeycloakAngularModule
    ),

    provideStore(reducers, { metaReducers }),
    provideState('auth', State.fromAuth.authReducer),
    provideState('invitation', State.fromInvitation.invitationReducer),
    provideState('user', State.fromUser.userReducer),
    provideState('reference', State.fromReference.referenceReducer),
    provideState('commodity', State.fromCommodity.commodityReducer),
    provideState('customer', State.fromCustomer.customerReducer),
    provideState('countries', State.fromCountries.countriesReducer),
    provideState('states', State.fromStates.statesReducer),
    provideState('timezones', State.fromTimezones.timezonesReducer),
    provideState('userProfile', State.fromUserProfile.userProfileReducer),
    provideState('onboarding', State.fromOnboarding.onboardingReducer),
    provideState('addressGroup', State.fromAddressGroup.addressGroupReducer),
    provideState('shipOrQuote', State.fromShipOrQuote.shipOrQuoteReducer),
    provideState('sameDayCourier', State.sameDayCourier.sameDayCourierReducer),
    provideState('coldChain', State.coldChain.specialtyColdChainReducer),
    provideState(
      'publicColdChain',
      State.publicColdChain.publicSpecialtyColdChainReducer
    ),
    provideState('shipments', State.shipments.shipmentsReducer),

    provideEffects(
      State.AuthEffects,
      State.UserProfileEffects,
      State.AddressGroupEffects,
      State.ShipOrQuoteEffects,
      State.SpecialtyColdChainEffects,
      State.CustomerEffects,
      State.StatesEffects,
      State.CountriesEffects,
      State.TimezonesEffects
    ),

    provideStoreDevtools({ logOnly: environment.production }),
  ],
};
